import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import L from "leaflet";
import { signIcon } from "@/components/map/SignObjects";
import { StaffMarkers } from "@/components/map/StaffMarkers";
import { MapContainer, TileLayer, Polyline, Marker, CircleMarker, useMap } from "react-leaflet";
import { Navigation, Clock, MapPin, Shield, LocateFixed } from "lucide-react";
import { API } from "@/lib/api";
import { CATEGORY, EVENT_TYPES } from "@/lib/constants";
import { unidadCompleta } from "@/lib/staff";

const num = (n, color, active) => L.divIcon({
  className: "",
  html: `<div class="tac-marker ${active ? "sel" : ""}" style="width:26px;height:26px;background:${color};color:${color}"><span style="color:#fff">${n}</span></div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

const dur = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
  return h ? `${h} h ${String(m).padStart(2, "0")} min` : `${m} min ${String(x).padStart(2, "0")} s`;
};

const Fit = ({ pts }) => {
  const map = useMap();
  useEffect(() => {
    const fit = () => { map.invalidateSize(); if (pts.length > 1) map.fitBounds(pts, { padding: [30, 30], maxZoom: 17 }); else if (pts[0]) map.setView(pts[0], 17); };
    const t = setTimeout(fit, 150);
    window.addEventListener("orientationchange", fit);
    return () => { clearTimeout(t); window.removeEventListener("orientationchange", fit); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pts.length]);
  return null;
};

export default function AgentMobile() {
  const { token, unit } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(Date.now());
  const [me, setMe] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/public/sheet/${token}/${unit}`).then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "No se pudo cargar la hoja"));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [token, unit]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition((p) => setMe([p.coords.latitude, p.coords.longitude]), () => {}, { enableHighAccuracy: true });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const segs = useMemo(() => {
    if (!data) return [];
    const base = new Date(`${data.event.date}T00:00:00`).getTime();
    return data.unit.segments.map((s, i) => ({ ...s, n: i + 1, t0: base + s.start * 60000, t1: base + s.end * 60000, td: s.depart != null ? base + s.depart * 60000 : null }));
  }, [data]);

  if (err) return <div className="min-h-screen bg-slate-50 text-slate-700 grid place-items-center p-6 text-center" data-testid="mobile-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-slate-50 text-slate-500 grid place-items-center font-mono text-xs">CARGANDO…</div>;

  const current = segs.find((s) => now >= s.t0 && now < s.t1);
  const next = segs.find((s) => s.t0 > now);
  const focus = current || next;
  const pts = segs.filter((s) => s.loc).map((s) => s.loc);
  const navUrl = focus?.loc ? `https://www.google.com/maps/dir/?api=1&destination=${focus.loc[0]},${focus.loc[1]}` : null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-10" data-testid="agent-mobile-page">
      <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-sky-600" />
          <div className="leading-tight">
            <div className="text-[0.625rem] font-mono tracking-widest text-sky-600">{data.event.town?.name ? `PL ${data.event.town.name.toUpperCase()}` : "POLICÍAS DE GALICIA"} · HOJA DE SERVICIO</div>
            <div className="text-sm font-semibold truncate max-w-[200px]">{data.event.name}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[0.5625rem] font-mono text-slate-500">TIP</div>
          <div className="font-display text-xl font-extrabold text-amber-700" data-testid="mobile-tip">{data.unit.tip || data.unit.nombre || "—"}</div>
          {data.unit.tip && data.unit.nombre && !data.unit.nombre.startsWith("TIP") && <div className="text-xs font-bold text-slate-800" data-testid="mobile-indicativo">{data.unit.nombre}</div>}
        </div>
      </div>

      <div className="px-4 py-4">
        <div className={`rounded-2xl p-4 border ${current ? "bg-red-500/10 border-red-500/40" : "bg-sky-500/10 border-sky-500/30"}`} data-testid="mobile-countdown">
          {current ? (
            <>
              <div className="text-[0.625rem] font-mono tracking-widest text-red-700">EN CURSO · PUESTO {current.n}</div>
              <div className="font-display text-lg font-bold mt-1">{current.name}</div>
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-700"><Clock className="w-4 h-4" /> Termina en <b className="font-mono text-2xl text-slate-900">{dur(current.t1 - now)}</b></div>
            </>
          ) : next ? (
            <>
              <div className="text-[0.625rem] font-mono tracking-widest text-sky-700">PRÓXIMA TAREA · PUESTO {next.n}</div>
              <div className="font-display text-lg font-bold mt-1">{next.name}</div>
              <div className="text-xs text-slate-700">Presentarse a las <b className="font-mono">{next.start_hm}</b></div>
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-700"><Clock className="w-4 h-4" /> Empieza en <b className="font-mono text-2xl text-slate-900" data-testid="mobile-countdown-value">{dur(next.t0 - now)}</b></div>
              {next.td && <div className="text-xs text-amber-700 mt-1">Salir a las {next.depart_hm} desde {next.from} ({next.travel_min} min)</div>}
            </>
          ) : (
            <div className="text-sm text-emerald-700" data-testid="mobile-finished">Servicio finalizado. Gracias.</div>
          )}
          {navUrl && (
            <a href={navUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 bg-sky-500 text-slate-950 font-semibold px-4 py-2 rounded-lg text-sm" data-testid="mobile-navigate-button">
              <Navigation className="w-4 h-4" /> Cómo llegar
            </a>
          )}
        </div>
      </div>

      <div className="mx-4 h-[300px] rounded-2xl overflow-hidden border border-slate-200 relative isolate z-0" data-testid="mobile-map">
        <MapContainer center={pts[0] || [42.3929, -8.7042]} zoom={16} maxZoom={22} style={{ height: "100%", width: "100%" }} attributionControl={false}>
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={22} maxNativeZoom={19} />
          <Fit pts={pts} />
          {data.route && <Polyline positions={data.route} pathOptions={{ color: "#0ea5e9", weight: 4, opacity: 0.5 }} />}
          {pts.length > 1 && <Polyline positions={pts} pathOptions={{ color: "#f8fafc", weight: 2, dashArray: "6 6" }} />}
          <StaffMarkers points={data.staff_points || []} />
          {(data.signs || []).map((g) => <Marker key={g.id} position={g.coords[0]} icon={signIcon(g, false)} interactive={false} />)}
          {segs.filter((s) => s.loc).map((s) => <Marker key={s.n} position={s.loc} icon={num(s.n, CATEGORY[s.category]?.color || "#334155", focus?.n === s.n)} />)}
          {me && <CircleMarker center={me} radius={8} pathOptions={{ color: "#fff", weight: 3, fillColor: "#22c55e", fillOpacity: 1 }} />}
        </MapContainer>
        <div className="absolute bottom-2 left-2 z-[500] text-[0.625rem] bg-white/95 px-2 py-1 rounded flex items-center gap-1 text-slate-700">
          <LocateFixed className="w-3 h-3 text-emerald-600" /> {me ? "Su posición en verde" : "Active la ubicación para verse en el mapa"}
        </div>
      </div>

      <div className="px-4 mt-5 space-y-2" data-testid="mobile-task-list">
        <div className="text-[0.625rem] font-mono tracking-widest text-slate-500">TURNO {data.unit.shift_hm} · {data.unit.tipo ? unidadCompleta(data.unit.tipo, data.unit.nombre) : data.unit.label.split(" · ")[0]} · {EVENT_TYPES[data.event.type]} {data.event.date}</div>
        {segs.map((s) => {
          const st = now >= s.t1 ? "done" : now >= s.t0 ? "live" : "todo";
          return (
            <div key={s.n} className={`rounded-xl p-3 border ${st === "live" ? "border-red-500/50 bg-red-500/10" : st === "done" ? "border-slate-200 opacity-50" : "border-slate-200 bg-white"}`} data-testid={`mobile-task-${s.n}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold">{s.n}. {s.start_hm}–{s.end_hm}</span>
                <span className="text-[0.625rem] px-2 py-0.5 rounded-full" style={{ background: `${CATEGORY[s.category]?.color}33`, color: CATEGORY[s.category]?.color }}>{CATEGORY[s.category]?.label}</span>
              </div>
              <div className="text-sm mt-1">{s.name}{s.handover ? " · RELEVO" : ""}</div>
              {s.notes && <div className="text-xs text-amber-800 bg-amber-500/10 border-l-2 border-amber-400 rounded px-2 py-1 mt-1.5 whitespace-pre-wrap" data-testid={`mobile-task-notes-${s.n}`}>{s.notes}</div>}
              {s.loc && <a className="text-[0.6875rem] text-sky-700 inline-flex items-center gap-1 mt-1" href={`https://www.google.com/maps/dir/?api=1&destination=${s.loc[0]},${s.loc[1]}`} target="_blank" rel="noreferrer"><MapPin className="w-3 h-3" /> Ir al puesto</a>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
